/**
 * Graphe d'ÉVOLUTION d'une stat (SVG pur, sans dépendance), forme ADAPTÉE à la
 * nature de la stat :
 *  - stat en POURCENTAGE (possession, passes) → BANDE DE MOMENTUM : tout l'espace
 *    est partagé domicile (vert, en haut) / extérieur (ambre, en bas), la
 *    frontière ondule → on voit d'un coup d'œil qui domine et quand ça bascule.
 *  - stat CUMULÉE (xG, tirs, corners…) → AIRES LISSÉES : deux courbes douces
 *    remplies (dégradé) avec points.
 * Communs : repère mi-temps (45'), REPÈRES DE BUTS sur la timeline, dégradés,
 * tracé révélé de gauche à droite (animation, coupée si reduced-motion).
 *
 * Fonction pure → testable. Couleurs via variables CSS (cohérent avec les barres).
 */
import { esc } from "../html.ts";
import { t } from "../../app/i18n.ts";

/** Libellé canonique + nature (pourcentage ?) par clé de stat. Fonction (pas const)
 *  pour que `t()` se résolve à la langue COURANTE au moment du rendu. */
export const statMeta = (): Record<string, { label: string; asPct: boolean }> => ({
  possession: { label: t("method.row.possession.s"), asPct: true },
  passAcc: { label: t("method.row.passAcc.s"), asPct: true },
  xg: { label: t("stats.statMeta.xg"), asPct: false },
  shots: { label: t("stats.col.shots"), asPct: false },
  shotsOnTarget: { label: t("stats.statMeta.shotsOnTarget"), asPct: false },
  blocked: { label: t("stats.statMeta.blocked"), asPct: false },
  corners: { label: t("stats.statMeta.corners"), asPct: false },
  fouls: { label: t("stats.statMeta.fouls"), asPct: false },
  offsides: { label: t("stats.statMeta.offsides"), asPct: false },
  yellow: { label: t("stats.statMeta.yellow"), asPct: false },
  red: { label: t("stats.statMeta.red"), asPct: false },
  saves: { label: t("stats.statMeta.saves"), asPct: false },
});

/** Ordre d'affichage des stats duo (carte live + panneau de match). Toutes celles
 *  fournies par le flux sont rendues ; `duoStat` ignore une stat absente. */
export const STAT_ORDER = [
  "possession",
  "passAcc",
  "xg",
  "shots",
  "shotsOnTarget",
  "blocked",
  "corners",
  "fouls",
  "offsides",
  "saves",
  "yellow",
  "red",
] as const;

export interface GoalMark {
  readonly minute: number;
  readonly team: "home" | "away";
}

const W = 264;
const H = 156;
const PAD = { l: 28, r: 12, t: 20, b: 18 };

type Series = ReadonlyArray<readonly [number, number | null, number | null]>; // [minute, dom, ext]
type Pt = readonly [number, number];

/** Catmull-Rom → courbe de Bézier lissée. */
function smooth(pts: readonly Pt[]): string {
  const first = pts[0];
  if (!first) return "";
  const f = (n: number): string => n.toFixed(1);
  if (pts.length === 1) return `M${f(first[0])},${f(first[1])}`;
  let d = `M${f(first[0])},${f(first[1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    if (!p0 || !p1 || !p2 || !p3) continue;
    d += ` C${f(p1[0] + (p2[0] - p0[0]) / 6)},${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)},${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])},${f(p2[1])}`;
  }
  return d;
}

const fmtVal = (v: number | null, asPct: boolean): string =>
  v == null ? "—" : asPct ? `${Math.round(v)}%` : Number.isInteger(v) ? `${v}` : v.toFixed(2);

function emptyGraph(label: string, isLive: boolean): string {
  // Match EN COURS avec <2 relevés : la courbe se construit (honnête, rassurant).
  // Sinon (match déjà terminé au lancement du suivi) : pas d'historique possible.
  const msg = isLive ? t("stats.graph.buildingTitle") : t("stats.graph.unavailableTitle");
  const sub = isLive
    ? t("stats.graph.buildingSub")
    : t("stats.graph.unavailableSub");
  return `<div class="sg-title">${esc(label)}</div><div class="sg-empty">${msg}<span class="sg-sub">${sub}</span></div>`;
}

/** Repères de buts (ligne verticale + pastille) sur la timeline. */
function goalMarks(goals: readonly GoalMark[], xMax: number, sx: (m: number) => number, yTop: number, yBase: number): string {
  return goals
    .filter((g) => typeof g.minute === "number" && g.minute <= xMax + 1)
    .map((g) => {
      const x = sx(Math.min(g.minute, xMax)).toFixed(1);
      return `<line class="sg-goal ${g.team}" x1="${x}" y1="${yTop}" x2="${x}" y2="${yBase}"/><circle class="sg-goal-d ${g.team}" cx="${x}" cy="${yTop + 1}" r="3"/>`;
    })
    .join("");
}

/**
 * Graphe d'évolution d'UNE stat. `goals` = buts (minute + camp) pour les repères.
 * Renvoie un message si moins de 2 relevés exploitables.
 */
export function statGraph(
  series: Series,
  key: string,
  homeCode: string,
  awayCode: string,
  goals: readonly GoalMark[] = [],
  isLive = false,
): string {
  const meta = statMeta()[key] ?? { label: key, asPct: false };
  if (series.length < 2) return emptyGraph(meta.label, isLive);

  const x0 = PAD.l;
  const x1 = W - PAD.r;
  const yTop = PAD.t;
  const yBase = H - PAD.b;
  const bandH = yBase - yTop;
  const xMax = Math.max(1, ...series.map((p) => p[0]));
  const sx = (m: number): number => x0 + (m / xMax) * (x1 - x0);
  const last = series[series.length - 1] ?? [0, null, null];

  const defs = `<defs>
    <linearGradient id="sgGH" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="var(--color-text-success)" stop-opacity="0.42"/><stop offset="1" stop-color="var(--color-text-success)" stop-opacity="0.04"/></linearGradient>
    <linearGradient id="sgGA" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="var(--color-text-warning)" stop-opacity="0.34"/><stop offset="1" stop-color="var(--color-text-warning)" stop-opacity="0.03"/></linearGradient>
  </defs>`;
  const htGuide =
    xMax >= 45
      ? `<line class="sg-guide" x1="${sx(45).toFixed(1)}" y1="${yTop}" x2="${sx(45).toFixed(1)}" y2="${yBase}"/>`
      : "";
  const xticks = `<text x="${x0}" y="${H - 5}" class="sg-tick">0′</text>${xMax >= 45 ? `<text x="${sx(45).toFixed(1)}" y="${H - 5}" class="sg-tick" text-anchor="middle">${t("common.halfTimeAbbr")}</text>` : ""}<text x="${x1}" y="${H - 5}" class="sg-tick" text-anchor="end">${Math.round(xMax)}′</text>`;
  const marks = goalMarks(goals, xMax, sx, yTop, yBase);
  const legend = `<div class="sg-legend"><span class="sg-k h">${esc(homeCode)} <b>${fmtVal(last[1], meta.asPct)}</b></span><span class="sg-k a">${esc(awayCode)} <b>${fmtVal(last[2], meta.asPct)}</b></span></div>`;

  let plot: string;
  if (meta.asPct) {
    // BANDE DE MOMENTUM : vert (domicile) en haut, ambre (extérieur) en bas ;
    // la frontière = part domicile sur le total à chaque instant.
    const bnd: Pt[] = series.map((p) => {
      const h = p[1] ?? 0;
      const a = p[2] ?? 0;
      const share = h + a > 0 ? h / (h + a) : 0.5;
      return [sx(p[0]), yTop + share * bandH];
    });
    const rev = bnd.slice().reverse();
    const greenD = `M${x0},${yTop} L${x1},${yTop} ${smooth(rev).replace(/^M/, "L")} Z`;
    plot = `<g class="sg-reveal">
      <rect class="sg-band-a" x="${x0}" y="${yTop}" width="${(x1 - x0).toFixed(1)}" height="${bandH.toFixed(1)}" rx="3"/>
      <path class="sg-band-h" d="${greenD}"/>
      <path class="sg-band-line" d="${smooth(bnd)}" pathLength="1"/>
      ${marks}
    </g>`;
  } else {
    // AIRES LISSÉES : deux courbes remplies (dégradé) + points.
    const vals = series.flatMap((p) => [p[1], p[2]]).filter((v): v is number => v != null);
    const yMax = Math.max(1, Math.ceil(Math.max(...vals) * 1.15));
    const sy = (v: number): number => yBase - (v / yMax) * bandH;
    const hp: Pt[] = series.filter((p) => p[1] != null).map((p) => [sx(p[0]), sy(p[1] as number)]);
    const ap: Pt[] = series.filter((p) => p[2] != null).map((p) => [sx(p[0]), sy(p[2] as number)]);
    const area = (pts: Pt[], grad: string): string => {
      const first = pts[0];
      const lastP = pts[pts.length - 1];
      if (pts.length < 2 || !first || !lastP) return "";
      return `<path fill="url(#${grad})" d="${smooth(pts)} L${lastP[0].toFixed(1)},${yBase} L${first[0].toFixed(1)},${yBase} Z"/>`;
    };
    const dots = (pts: Pt[], cls: string): string =>
      pts.map((p) => `<circle class="sg-dot ${cls}" cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.4"/>`).join("");
    plot = `<g class="sg-reveal">
      ${area(ap, "sgGA")}${area(hp, "sgGH")}
      <path class="sg-line a" d="${smooth(ap)}" pathLength="1"/>
      <path class="sg-line h" d="${smooth(hp)}" pathLength="1"/>
      ${dots(ap, "a")}${dots(hp, "h")}
      ${marks}
      <text x="${x0 - 4}" y="${yTop + 7}" class="sg-tick" text-anchor="end">${yMax}</text>
    </g>`;
  }

  return `<div class="sg-title">${esc(meta.label)}</div>
<svg viewBox="0 0 ${W} ${H}" class="sg-svg" role="img" aria-label="${esc(t("stats.graph.ariaLabel", { label: meta.label }))}">
  ${defs}
  <line class="sg-axis" x1="${x0}" y1="${yBase}" x2="${x1}" y2="${yBase}"/>
  ${htGuide}
  ${plot}
  ${xticks}
</svg>${legend}`;
}
